import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import { SectionCard, SecondaryButton, Tile, TileGrid } from "@/components/ds";
import { ChevronDown } from "@/components/ds/icons";

interface ShopperSummary {
  totalCustomers: number;
  totalLtv: number;
  avgOrderValue: number;
  activeCarts: number;
  lastSyncedAt: string | null;
}

interface EcommerceContact {
  id: number;
  contactId: number | null;
  platform: number;
  platformName: string;
  externalCustomerId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  totalSpent: number | null;
  orderCount: number;
  lastOrderDate: string | null;
  abandonedCartCount: number;
  tagsJson: string | null;
  syncedAt: string;
}

interface EcommerceOrder {
  id: number;
  externalOrderId: string;
  total: number | null;
  currency: string | null;
  status: string;
  lineItemsJson: string | null;
  placedAt: string | null;
}

interface ContactsResponse {
  total: number;
  page: number;
  pageSize: number;
  contacts: EcommerceContact[];
}

interface Props {
  platform: string;
}

export default function ShopperProfileTab({ platform }: Props) {
  const api = useApi();
  const [summary, setSummary] = useState<ShopperSummary | null>(null);
  const [contacts, setContacts] = useState<EcommerceContact[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [orders, setOrders] = useState<Record<number, EcommerceOrder[]>>({});
  const [ordersLoading, setOrdersLoading] = useState<Record<number, boolean>>(
    {}
  );
  const PAGE_SIZE = 50;

  const fetchData = useCallback(
    async (p: number) => {
      setLoading(true);
      setLoadError(null);
      try {
        const [summaryRes, contactsRes] = await Promise.all([
          api.messaging.get(`EcommerceData/summary?platform=${platform}`),
          api.messaging.get(
            `EcommerceData/contacts?platform=${platform}&page=${p}&pageSize=${PAGE_SIZE}`
          )
        ]);
        if (summaryRes.ok) {
          setSummary(await summaryRes.json());
        } else {
          const body = await summaryRes.text().catch(() => "");
          setLoadError(
            `Failed to load shopper summary: ${summaryRes.status}${body ? ` — ${body}` : ""}`
          );
        }
        if (contactsRes.ok) {
          const data: ContactsResponse = await contactsRes.json();
          setContacts(Array.isArray(data.contacts) ? data.contacts : []);
          setTotal(data.total ?? 0);
        } else {
          const body = await contactsRes.text().catch(() => "");
          setLoadError(
            `Failed to load shoppers: ${contactsRes.status}${body ? ` — ${body}` : ""}`
          );
          setContacts([]);
        }
      } catch (err) {
        console.error("ShopperProfileTab: load failed", err);
        setLoadError(
          `Failed to load shoppers: ${err instanceof Error ? err.message : String(err)}`
        );
        setContacts([]);
      } finally {
        setLoading(false);
      }
    },
    [api.messaging, platform]
  );

  useEffect(() => {
    fetchData(page);
  }, [fetchData, page]);

  const toggleExpand = useCallback(
    async (id: number) => {
      if (expandedId === id) {
        setExpandedId(null);
        return;
      }
      setExpandedId(id);
      if (!orders[id]) {
        setOrdersLoading((prev) => ({ ...prev, [id]: true }));
        try {
          const res = await api.messaging.get(
            `EcommerceData/contacts/${id}/orders`
          );
          if (res.ok) {
            const data = await res.json();
            setOrders((prev) => ({ ...prev, [id]: data }));
          }
        } catch (err) {
          console.error("ShopperProfileTab: orders fetch failed", err);
        } finally {
          setOrdersLoading((prev) => ({ ...prev, [id]: false }));
        }
      }
    },
    [api.messaging, expandedId, orders]
  );

  const fmt = (n: number | null | undefined, prefix = "$") =>
    n != null
      ? `${prefix}${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : "—";

  const fmtDate = (d: string | null | undefined) =>
    d
      ? new Date(d).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric"
        })
      : "—";

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const stats = [
    {
      label: "Total shoppers",
      value: summary ? summary.totalCustomers.toLocaleString() : "—"
    },
    { label: "Total LTV", value: fmt(summary?.totalLtv) },
    { label: "Avg order value", value: fmt(summary?.avgOrderValue) },
    {
      label: "Abandoned carts",
      value: summary ? summary.activeCarts.toLocaleString() : "—"
    }
  ];

  return (
    <div className="space-y-4">
      <SectionCard
        title="Summary"
        description={
          summary?.lastSyncedAt
            ? `Last synced ${new Date(summary.lastSyncedAt).toLocaleString()}.`
            : undefined
        }
      >
        <TileGrid columns={{ base: 2, md: 4 }} gap={3}>
          {stats.map((stat) => (
            <Tile key={stat.label}>
              <Tile.Body>
                <Tile.Description>{stat.label}</Tile.Description>
                <Tile.Title>{stat.value}</Tile.Title>
              </Tile.Body>
            </Tile>
          ))}
        </TileGrid>
      </SectionCard>

      <SectionCard
        title="Shoppers"
        description={total > 0 ? `${total.toLocaleString()} total` : undefined}
      >
        {loading ? (
          <div className="flex items-center justify-center py-12 text-zinc-400 text-sm">
            Loading shoppers…
          </div>
        ) : loadError ? (
          <div
            className="flex flex-col items-center justify-center py-12"
            role="alert"
          >
            <div className="text-sm font-medium mb-1 text-amber-700">
              Couldn&apos;t load shoppers
            </div>
            <div className="text-xs text-amber-600">{loadError}</div>
          </div>
        ) : contacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-zinc-400">
            <div className="text-sm font-medium mb-1">
              No shoppers synced yet
            </div>
            <div className="text-xs text-zinc-400">
              Ecommerce sync runs hourly. Check back soon.
            </div>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_0.5fr] gap-2 px-4 py-2 bg-zinc-50 text-xs font-semibold text-zinc-500 uppercase tracking-wide rounded-t-md">
              <div>Shopper</div>
              <div>LTV</div>
              <div>Orders</div>
              <div>Last order</div>
              <div>Carts</div>
              <div></div>
            </div>

            {contacts.map((c) => (
              <React.Fragment key={c.id}>
                <div
                  className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_0.5fr] gap-2 px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer items-center"
                  onClick={() => toggleExpand(c.id)}
                >
                  <div>
                    <div className="text-sm font-medium text-zinc-800">
                      {[c.firstName, c.lastName].filter(Boolean).join(" ") ||
                        "Unknown"}
                    </div>
                    <div className="text-xs text-zinc-400">
                      {c.email ?? "—"}
                    </div>
                    {c.tagsJson &&
                      (() => {
                        try {
                          const tags: string[] = JSON.parse(c.tagsJson);
                          return (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {tags.slice(0, 3).map((t) => (
                                <span
                                  key={t}
                                  className="text-[10px] bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-full"
                                >
                                  {t}
                                </span>
                              ))}
                            </div>
                          );
                        } catch (err) {
                          console.error(
                            "ShopperProfileTab: tags parse failed",
                            err
                          );
                          return null;
                        }
                      })()}
                  </div>
                  <div className="text-sm font-semibold text-zinc-700">
                    {fmt(c.totalSpent)}
                  </div>
                  <div className="text-sm text-zinc-700">{c.orderCount}</div>
                  <div className="text-sm text-zinc-500">
                    {fmtDate(c.lastOrderDate)}
                  </div>
                  <div className="text-sm">
                    {c.abandonedCartCount > 0 ? (
                      <span className="text-amber-600 font-medium">
                        {c.abandonedCartCount}
                      </span>
                    ) : (
                      <span className="text-zinc-400">0</span>
                    )}
                  </div>
                  <div className="flex justify-end">
                    <ChevronDown
                      className={`w-4 h-4 text-zinc-400 transition-transform ${expandedId === c.id ? "rotate-180" : ""}`}
                    />
                  </div>
                </div>

                {expandedId === c.id && (
                  <div className="bg-zinc-50 border-b border-zinc-100 px-6 py-4">
                    <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-3">
                      Order history
                    </div>
                    {ordersLoading[c.id] ? (
                      <div className="text-xs text-zinc-400">
                        Loading orders…
                      </div>
                    ) : (orders[c.id] ?? []).length === 0 ? (
                      <div className="text-xs text-zinc-400">
                        No orders found.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(orders[c.id] ?? []).map((o) => (
                          <div
                            key={o.id}
                            className="flex items-center gap-4 bg-white border border-zinc-200 px-3 py-2 text-xs rounded"
                          >
                            <div className="font-mono text-zinc-600 shrink-0">
                              #{o.externalOrderId}
                            </div>
                            <div className="font-semibold text-zinc-700">
                              {fmt(o.total)}
                              {o.currency ? ` ${o.currency}` : ""}
                            </div>
                            <div
                              className={`px-1.5 py-0.5 rounded-full font-medium ${
                                o.status === "fulfilled"
                                  ? "bg-green-100 text-green-700"
                                  : o.status === "cancelled"
                                    ? "bg-red-100 text-red-600"
                                    : "bg-blue-100 text-blue-700"
                              }`}
                            >
                              {o.status}
                            </div>
                            <div className="text-zinc-400 ml-auto">
                              {fmtDate(o.placedAt)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-xs text-zinc-500">
              Page {page} of {totalPages} ({total.toLocaleString()} shoppers)
            </div>
            <div className="flex gap-2">
              <SecondaryButton
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="text-xs !px-3 !py-1.5"
              >
                Previous
              </SecondaryButton>
              <SecondaryButton
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="text-xs !px-3 !py-1.5"
              >
                Next
              </SecondaryButton>
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
